import React, { useState } from 'react';
import { AutoMarkingResponse } from '../../../services/autoMarking';

interface StudentSummaryProps {
  result: AutoMarkingResponse;
}

const StudentSummary: React.FC<StudentSummaryProps> = ({ result }) => {
  const [expandedFeedback, setExpandedFeedback] = useState<number | null>(null);

  const toggleFeedback = (index: number) => {
    setExpandedFeedback(expandedFeedback === index ? null : index);
  };

  if (result.error) {
    return (
      <div className="tab-pane-content">
        <div className="alert alert-danger">
          <h5><i className="fas fa-exclamation-triangle mr-2"></i>Processing Error</h5>
          <p>{result.error}</p>
        </div>
      </div>
    );
  }

  if (!result.data || !result.data.summary) {
    return (
      <div className="tab-pane-content">
        <div className="alert alert-warning">
          <h5><i className="fas fa-info-circle mr-2"></i>No Data Available</h5>
          <p>Module and summary data is not available for this file.</p>
        </div>
      </div>
    );
  }

  const { data } = result;
  const { summary } = data;
  const questionDetails = data.questionDetails || [];
  
  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'correct':
        return <i className="fas fa-check-circle text-success"></i>;
      case 'partial':
        return <i className="fas fa-exclamation-circle text-warning"></i>;
      case 'incorrect':
        return <i className="fas fa-times-circle text-danger"></i>;
      default:
        return <i className="fas fa-question-circle text-muted"></i>;
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'correct':
        return <span className="badge badge-success">Correct</span>;
      case 'partial':
        return <span className="badge badge-warning">Partial</span>;
      case 'incorrect':
        return <span className="badge badge-danger">Incorrect</span>;
      default:
        return <span className="badge badge-secondary">Unknown</span>;
    }
  };

  return (
    <div className="tab-pane-content">
      <div className="student-overview">
        {/* Merged Module Information and Performance Summary */}
        <div className="card">
          <div className="card-header" style={{ backgroundColor: '#17a2b8', color: '#fff', borderColor: '#17a2b8' }}>
            <h6 className="mb-0 text-white"><i className="fas fa-book mr-2"></i>Module Information & Performance Summary</h6>
          </div>
          <div className="card-body">
            {/* Module Info Row - Split into 4 columns */}
            <div className="row mb-3 pb-3 border-bottom">
              <div className="col-md-3">
                <p className="mb-0"><strong>Subject:</strong> {data?.subject_name || 'N/A'}</p>
              </div>
              <div className="col-md-3">
                <p className="mb-0"><strong>Module:</strong> {data.module || 'N/A'}</p>
              </div>
              {/* <div className="col-md-3">
                <p className="mb-0"><strong>Module Code:</strong> {data.moduleCode || 'N/A'}</p>
              </div> */}
              <div className="col-md-3">
                <p className="mb-0"><strong>Worksheet No:</strong> {data.pageNumber || 'N/A'}</p>
              </div>
              <div className="col-md-3">
                <p className="mb-0"><strong>Handbook Page:</strong> {data.handbookPage || 'N/A'}</p>
              </div>
            </div>

            {/* Performance Stats Row */}
            <div className="row mb-3">
              <div className="col text-center">
                <h4 className="text-primary">{summary.totalQuestions}</h4>
                <p className="mb-0">Total Questions</p>
              </div>
              <div className="col text-center">
                <h4 className="text-success">{summary.correctAnswers}</h4>
                <p className="mb-0">Correct</p>
              </div>
              <div className="col text-center">
                <h4 className="text-warning">{summary.partiallyCorrect}</h4>
                <p className="mb-0">Partial</p>
              </div>
              <div className="col text-center">
                <h4 className="text-danger">{summary.incorrect}</h4>
                <p className="mb-0">Incorrect</p>
              </div>
              <div className="col text-center">
                <h4 className="text-secondary">{summary.unanswered}</h4>
                <p className="mb-0">Unanswered</p>
              </div>
            </div>

            {/* Progress Bar */}
            <div className="mt-3">
              <h6>Performance Breakdown</h6>
              <div className="progress" style={{ height: '25px' }}>
                <div 
                  className="progress-bar bg-success" 
                  style={{ width: `${(summary.correctAnswers / summary.totalQuestions) * 100}%` }}
                >
                  {summary.correctAnswers} Correct
                </div>
                <div 
                  className="progress-bar bg-warning" 
                  style={{ width: `${(summary.partiallyCorrect / summary.totalQuestions) * 100}%` }}
                >
                  {summary.partiallyCorrect} Partial
                </div>
                <div 
                  className="progress-bar bg-danger" 
                  style={{ width: `${(summary.incorrect / summary.totalQuestions) * 100}%` }}
                >
                  {summary.incorrect} Incorrect
                </div>
                {summary.unanswered > 0 && (
                  <div 
                    className="progress-bar bg-secondary" 
                    style={{ width: `${(summary.unanswered / summary.totalQuestions) * 100}%` }}
                  >
                    {summary.unanswered} Unanswered
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Questions List */}
        {questionDetails.length > 0 && (
          <div className="questions-list mt-4">
            <div className="d-flex justify-content-between align-items-center mb-3">
              <h5>
                <i className="fas fa-list-ol mr-2"></i>
                Questions ({questionDetails.length})
              </h5>
            </div>

            {questionDetails.map((question, index) => {
              const getHeaderColor = () => {
                switch (question.status) {
                  case 'correct': return '#d4edda'; // Light green
                  case 'partial': return '#fff3cd'; // Light yellow
                  case 'incorrect': return '#f8d7da'; // Light red
                  default: return '#e2e3e5'; // Light gray
                }
              };

              const getTextColor = () => {
                switch (question.status) {
                  case 'correct': return '#155724'; // Dark green text
                  case 'partial': return '#856404'; // Dark yellow text
                  case 'incorrect': return '#721c24'; // Dark red text
                  default: return '#383d41'; // Dark gray text
                }
              };

              const headerColor = getHeaderColor();
              const textColor = getTextColor();

              return (
                <div key={index} className="card mb-3">
                  <div 
                    className="card-header py-2 question-card-header" 
                    style={{
                      backgroundColor: headerColor,
                      color: textColor,
                      borderColor: headerColor,
                      backgroundImage: 'none'
                    }}
                  >
                    <div className="d-flex justify-content-between align-items-center" style={{ color: textColor }}>
                      <div style={{ color: textColor }}>
                        {getStatusIcon(question.status)}
                        <strong className="ml-2" style={{ color: textColor }}>Question {question.questionNumber}</strong>
                        <span className="badge badge-secondary ml-2">{question.taskType}</span>
                      </div>
                      <div>
                        {getStatusBadge(question.status)}
                      </div>
                    </div>
                  </div>
                <div className="card-body py-3">
                  <div className="mb-2">
                    <strong><i className="fas fa-question mr-2"></i>Question:</strong>
                    <span className="ml-2">{question.questionText}</span>
                  </div>
                  
                  <div className="row">
                    <div className="col-md-6">
                      <strong><i className="fas fa-user-edit mr-2"></i>Student Answer:</strong>
                      <div className={`p-2 mt-1 rounded border ${
                        question.status === 'correct' ? 'border-success' : 
                        question.status === 'partial' ? 'border-warning' : 
                        question.status === 'incorrect' ? 'border-danger' :
                        'border-secondary'
                      }`} style={{
                        backgroundColor: question.status === 'correct' ? '#d4edda' : 
                                        question.status === 'partial' ? '#fff3cd' : 
                                        question.status === 'incorrect' ? '#f8d7da' :
                                        '#f8f9fa',
                        color: question.status === 'correct' ? '#155724' : 
                              question.status === 'partial' ? '#856404' : 
                              question.status === 'incorrect' ? '#721c24' :
                              '#212529'
                      }}>
                        {question.studentAnswer || 'No answer provided'}
                      </div>
                    </div>
                    <div className="col-md-6">
                      <strong><i className="fas fa-check-circle mr-2"></i>Correct Answer:</strong>
                      <div className="border border-primary p-2 mt-1 rounded" style={{
                        backgroundColor: '#cfe2ff',
                        color: '#084298'
                      }}>
                        {question.correctAnswer}
                      </div>
                    </div>
                  </div>

                  {question.feedback && question.status !== 'correct' && (
                    <div className="mt-2">
                      <button 
                        className="btn btn-sm"
                        style={{ backgroundColor: '#fff3cd', color: '#856404', borderColor: '#ffeaa7' }}
                        onClick={() => toggleFeedback(index)}
                      >
                        <i className={`fas fa-comment-alt mr-1`}></i>
                        {expandedFeedback === index ? 'Hide Feedback' : 'Show Feedback'}
                      </button>
                      {expandedFeedback === index && (
                        <div className="p-2 mt-2 rounded border border-warning" style={{ backgroundColor: '#fff3cd', color: '#856404' }}>
                          {question.feedback}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default StudentSummary;